import React, { useMemo, useState, useRef } from "react";
import { Typography, Icon, Button } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { strategyConnect } from "../../providers/StrategyContextProvider";
import {
	flexRender,
	getCoreRowModel,
	useReactTable,
	getSortedRowModel,
	getExpandedRowModel,
} from "@tanstack/react-table";
import { formatCurrency, formatNumber } from "app/main/utils/tableUtils";
import { addToCart, removeFromCart, saveStrategy, emptyCart } from "../../providers/reducer/actions";
import StrategySummaryContainer from "../strategy-summary/StrategySummaryContainer";
import { NARROW_FIELDS } from "@fuse/default-settings";
import { useSelector } from "react-redux";
import { useContext } from "react";
import { StrategyActionContext, StrategyStateContext } from "../../providers/StrategyContextProvider";
import { selectByType } from "app/store/marketConditions/mapSlice";
import { createMPIView, createMPIViewManual } from "app/main/utils/brokerUtils";
import { validateMove } from "app/services/moveServices";

const useStyles = makeStyles((theme) => ({
	hover: {
		"&:hover:not(.active)": {
			backgroundColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.06)" : "rgba(0,0,0,.05)",
			cursor: "pointer",
		},
	},
	flexRow: {
		backgroundColor: theme.palette.type === "dark" ? "rgba(255, 165, 0, 0.33)" : "rgba(255, 165, 0, 0.2)",
	},
	table: {
		width: "100%",
		borderCollapse: "collapse",
	},
	header: {
		position: "sticky",
		top: 0,
		zIndex: 1,
		backgroundColor: theme.palette.background.paper,
		borderBottom: `1px solid ${theme.palette.divider}`,
	},
	cell: {
		padding: theme.spacing(1),
		textAlign: "left",
	},
	expandButton: {
		cursor: "pointer",
		padding: theme.spacing(0.5),
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	},
	subRow: {
		backgroundColor: theme.palette.background.default,
	},
	subRowCell: {
		padding: theme.spacing(1),
		borderTop: `1px solid ${theme.palette.divider}`,
	},
	tableContainer: {
		height: "600px",
		overflow: "auto",
	},
	cartButton: {
		marginLeft: theme.spacing(1),
		//backgroundColor: theme.palette.success.main,
		color: theme.palette.success.contrastText,
		// "&:hover": {
		// 	backgroundColor: theme.palette.success.dark,
		// },
	},
	approveButton: {
		//backgroundColor: theme.palette.success.main,
		//color: theme.palette.success.contrastText,
		// "&:hover": {
		// 	backgroundColor: theme.palette.success.dark,
		// },
		padding: theme.spacing(1, 2),
		borderRadius: theme.shape.borderRadius,
		textTransform: "none",
		fontWeight: 600,
		boxShadow: theme.shadows[2],
		"&:active": {
			boxShadow: theme.shadows[4],
		},
	},
	buttonNormal: {
		borderRadius: "4px",
		height: NARROW_FIELDS ? "4.6rem" : "5.2rem",
		backgroundColor: theme.palette.success.main,
		color: theme.palette.success.contrastText,
		"&:hover": {
			backgroundColor: theme.palette.success.dark,
		},
		textTransform: "none",
		fontWeight: 600,
	},
	buttonDisabled: {
		background: theme.palette.grey["400"] + " !important",
	},
	submitButton: {
		backgroundColor: theme.palette.primary.main,
		color: theme.palette.primary.contrastText,
		"&:hover": {
			backgroundColor: theme.palette.primary.dark,
		},
		padding: theme.spacing(1, 2),
		borderRadius: theme.shape.borderRadius,
		textTransform: "none",
		fontWeight: 500,
		minWidth: "120px",
		boxShadow: theme.shadows[1],
		"&:active": {
			boxShadow: theme.shadows[2],
		},
	},
	validateButton: {
		backgroundColor: theme.palette.info.main,
		color: theme.palette.info.contrastText,
		"&:hover": {
			backgroundColor: theme.palette.info.dark,
		},
		padding: theme.spacing(0.5, 1),
		borderRadius: theme.shape.borderRadius,
		textTransform: "none",
		fontWeight: 500,
		minWidth: "100px",
	},
	validatedRow: {
		backgroundColor: theme.palette.type === "dark" ? "rgba(76, 175, 80, 0.2)" : "rgba(76, 175, 80, 0.1)",
	},
}));

const StrategyList = ({ result, saveStrategy }) => {
	const classes = useStyles();
	const [sorting, setSorting] = useState([]);
	const [expanded, setExpanded] = useState({});
	const [validatedLegs, setValidatedLegs] = useState({});
	const summaryRef = useRef();
	const stack = useSelector(({ tools }) => tools.formDialog.formStack ?? []);
	const [mountStackLength] = useState(stack.length);
	const { selectedPaths } = useContext(StrategyStateContext);
	const dispatch = useContext(StrategyActionContext);

	// Get equipment from result and normalize it
	const equipment = useMemo(() => {
		const rawEquipment = result?.resData?.strategy?.equipment;
		if (!rawEquipment) return null;

		// Normalize equipment type to match market conditions API expectations
		const normalizedEquipment = rawEquipment.toLowerCase();
		return normalizedEquipment;
	}, [result?.resData?.strategy?.equipment]);

	// Add market conditions selector with normalized equipment
	const mapData = useSelector(({ marketConditions }) =>
		equipment ? selectByType(marketConditions, equipment, "ocluster", "toplanes") : null
	);

	const getPathMoves = (currentNode, pathsIndex = {}, currentPath = []) => {
		let path = [...currentPath];
		let node = { depth: path.length, isRoot: path.length === 0, data: currentNode };
		path.push(node);
		if (currentNode.stop.pathId !== null) {
			pathsIndex[currentNode.stop.pathId] = path;
		}
		currentNode.children.forEach((child) => {
			// concat the path with the child path
			getPathMoves(child, pathsIndex, path);
		});
		return path;
	};
	const pathsFromTree = useMemo(() => {
		const tree = result?.tree;
		if (!tree) return [];
		let pathsIndex = {};
		getPathMoves(tree, pathsIndex);
		return pathsIndex;
	}, [result]);

	const data = useMemo(() => {
		const paths = result?.resData?.strategy?.paths || [];
		const rawPaths = result?.resData?.strategy?.response?.paths || [];
		const contracts = result?.resData?.strategy?.response?.contracts || [];
		const isSaved = result?.resData?.isMongo || false;
		return paths.map((path, index) => ({
			...path,
			...rawPaths[index],
			visited_clusters: contracts.find((c) => c["Contract Id"] === path.path_id && c.Type === "C")?.Visited,
			id: path.path_id,
			isSelected: !!selectedPaths?.[path.path_id] || false,
			isSaved: isSaved,
			subRows: path.moves?.map((move, index) => ({
				...move.lane,
				id: `${path.path_id}-move-${index}`,
				isSubRow: true,
			})),
		}));
	}, [pathsFromTree, result, selectedPaths]);

	const columns = useMemo(
		() => [
			{
				id: "expander",
				header: () => null,
				cell: ({ row }) => {
					return row.getCanExpand() ? (
						<div className={classes.expandButton} onClick={row.getToggleExpandedHandler()}>
							<Icon>{row.getIsExpanded() ? "expand_less" : "expand_more"}</Icon>
						</div>
					) : null;
				},
				size: 50,
			},
			{
				accessorKey: "path_id",
				header: "Id",
				size: 100,
				sortingFn: "basic",
			},
			{
				accessorKey: "origin",
				header: "Origin",
				size: 150,
				cell: (info) => `${info.row.original.moves[0]?.o_city}`,
			},
			{
				accessorKey: "start_date",
				header: "Date Pickup",
				size: 120,
				sortingFn: "datetime",
				cell: (info) => new Date(info.getValue()).toLocaleDateString(),
			},
			{
				accessorKey: "end_date",
				header: "Date Delivery",
				size: 120,
				sortingFn: "datetime",
				cell: (info) => new Date(info.getValue()).toLocaleDateString(),
			},

			{
				accessorKey: "end_cluster",
				header: "Delivery",
				size: 150,
				cell: (info) => {
					const endCluster = info.row.original.end_cluster;
					const ppm_best_norm_smooth = mapData?.heatMap?.[endCluster]?.profit_best_norm_smooth;
					const volume = mapData?.heatMap?.[endCluster]?.vol_perc_smooth;
					return (
						<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
							{`${info.row.original.moves[info.row.original.moves.length - 1]?.d_city}`}
							{createMPIViewManual(ppm_best_norm_smooth, volume, true, false)}
						</div>
					);
				},
			},
			{
				accessorKey: "num_legs",
				header: "Legs",
				size: 50,
				sortingFn: "basic",
				cell: (info) => {
					const numLegs = info.row.original.num_legs;
					return numLegs ? numLegs.toFixed(0) : "N/A";
				},
			},
			{
				accessorKey: "total_duration",
				header: "Duration",
				size: 100,
				sortingFn: "basic",
				cell: (info) => {
					const duration = info.row.original.total_duration;
					return duration ? duration.toFixed(0) : "N/A";
				},
			},
			{
				accessorKey: "total_empty_miles",
				header: "Empty",
				size: 100,
				sortingFn: "basic",
				cell: (info) => {
					const miles = info.row.original.total_empty_miles;
					return miles ? formatNumber(miles, 0) : "N/A";
				},
			},
			{
				accessorKey: "total_loaded_miles",
				header: "Loaded",
				size: 100,
				sortingFn: "basic",
				cell: (info) => {
					const miles = info.row.original.total_loaded_miles;
					return miles ? formatNumber(miles, 0) : "N/A";
				},
			},
			{
				accessorKey: "min_payment",
				header: "Min Payment",
				size: 100,
				sortingFn: "basic",
				cell: (info) => {
					const payment = info.row.original.min_payment;
					return payment ? formatCurrency(payment, 0) : "N/A";
				},
			},
			{
				accessorKey: "total_payment",
				header: "Payment",
				size: 100,
				sortingFn: "basic",
				cell: (info) => {
					const payment = info.row.original.total_payment;
					return payment ? formatCurrency(payment, 0) : "N/A";
				},
			},
			{
				accessorKey: "max_payment",
				header: "Max Payment",
				size: 100,
				sortingFn: "basic",
				cell: (info) => {
					const payment = info.row.original.max_payment;
					return payment ? formatCurrency(payment, 0) : "N/A";
				},
			},
			{
				accessorKey: "total_rpm",
				header: "RPM",
				size: 100,
				sortingFn: "basic",
				cell: (info) => {
					const rpm = info.row.original.total_rpm;
					return rpm ? formatCurrency(rpm, 2) : "N/A";
				},
			},
			{
				accessorKey: "avg_volume",
				header: "Daily Volume",
				size: 100,
				sortingFn: "basic",
				cell: (info) => {
					const avgVolume = info.row.original.avg_volume;
					return avgVolume ? formatNumber(avgVolume, 0) : "N/A";
				},
			},
			{
				accessorKey: "visited",
				header: "Visited",
				size: 150,
				cell: (info) => info.row.original.visited_clusters?.join(", ") || "N/A",
			},
			{
				id: "actions",
				header: "Actions",
				size: 150,
				cell: ({ row }) => (
					<div>
						<Button
							variant="contained"
							color={row.original.isSelected ? "primary" : "secondary"}
							className={classes.approveButton}
							disabled={false} //row.original.isSaved
							onClick={(e) => {
								e.stopPropagation();
								handleApprove(row.original);
							}}
						>
							{row.original.isSelected ? "Remove" : "Select"}
						</Button>
					</div>
				),
			},
		],
		[classes.expandButton, classes.approveButton, mapData]
	);

	const legColumns = useMemo(
		() => [
			{
				accessorKey: "o_city",
				header: "Origin",
				cell: (info) => `${info.getValue()}`,
			},
			{
				accessorKey: "start_date",
				header: "Date",
				cell: (info) => new Date(info.getValue()).toLocaleString(),
			},
			{
				accessorKey: "d_city",
				header: "Destination",
				cell: (info) => {
					const dcluster = info.row.original.dcluster;
					const ppm_best_norm_smooth = mapData?.heatMap?.[dcluster]?.profit_best_norm_smooth;
					const volume = mapData?.heatMap?.[dcluster]?.vol_perc_smooth;
					return (
						<div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
							{`${info.getValue()}`}
							{createMPIViewManual(ppm_best_norm_smooth, volume, true, false)}
						</div>
					);
				},
			},
			{
				accessorKey: "end_date",
				header: "End Date",
				cell: (info) => new Date(info.getValue()).toLocaleString(),
			},
			{
				accessorKey: "loaded_miles",
				header: "Loaded",
				cell: (info) => formatNumber(info.getValue(), 0),
			},
			{
				accessorKey: "empty_miles",
				header: "Empty",
				cell: (info) => formatNumber(info.getValue(), 0),
			},
			{
				accessorKey: "avg_volume",
				header: "Daily Volume",
				cell: (info) => formatNumber(info.getValue(), 0),
			},
			{
				accessorKey: "min_payment",
				header: "Min Payment",
				cell: (info) => formatCurrency(info.getValue(), 0),
			},
			{
				accessorKey: "payment",
				header: "Payment",
				cell: (info) => formatCurrency(info.getValue(), 0),
			},
			{
				accessorKey: "max_payment",
				header: "Max Payment",
				cell: (info) => formatCurrency(info.getValue(), 0),
			},
			{
				accessorKey: "rpm",
				header: "RPM",
				cell: (info) => {
					const validatedData = validatedLegs[info.row.original.id];
					return validatedData?.rpm ? formatCurrency(validatedData.rpm, 2) : formatCurrency(info.getValue(), 2);
				},
			},
			{
				accessorKey: "avg_volume",
				header: "Daily Volume",
				cell: (info) => {
					const validatedData = validatedLegs[info.row.original.id];
					return validatedData?.avg_volume
						? formatNumber(validatedData.avg_volume, 0)
						: formatNumber(info.getValue(), 0);
				},
			},
			{
				id: "validate",
				header: "Validate",
				cell: (info) => (
					<Button
						variant="contained"
						className={classes.validateButton}
						onClick={(e) => {
							e.stopPropagation();
							handleValidateLeg(info.row.original);
						}}
					>
						{validatedLegs[info.row.original.id]?.isValidated ? "Validated" : "Validate"}
					</Button>
				),
			},
		],
		[mapData, classes.validateButton, validatedLegs]
	);

	const table = useReactTable({
		data,
		columns,
		state: {
			sorting,
			expanded,
		},
		onSortingChange: setSorting,
		onExpandedChange: setExpanded,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getExpandedRowModel: getExpandedRowModel(),
		getSubRows: (row) => row.subRows,
	});

	const handleApprove = (row) => {
		const pathId = row.path_id;
		const rawPaths = result?.resData?.strategy?.paths || result?.resData?.strategy?.response?.paths || [];
		const matchingMoves = pathsFromTree[pathId];

		if (matchingMoves) {
			if (row.isSelected) {
				dispatch(removeFromCart(pathId));
			} else {
				dispatch(addToCart(matchingMoves, pathId));
			}
		}
	};

	const handleValidateLeg = async (leg) => {
		try {
			const validationData = {
				legData: {
					origin: leg.o_city,
					destination: leg.d_city,
					startDate: leg.start_date,
					endDate: leg.end_date,
					loadedMiles: leg.loaded_miles,
					emptyMiles: leg.empty_miles,
					payment: leg.payment,
				},
				carrier: result?.resData?.strategy?.carrier,
				truck: result?.resData?.strategy?.truck,
			};

			const validationResult = await validateMove(validationData);

			if (validationResult?.data?.stats) {
				const { stats } = validationResult.data;
				const validatedData = {
					rpm: stats.rpmStats.mean,
					payment: stats.rate.mean,
					min_payment: stats.rate.q5,
					max_payment: stats.rate.q75,
					avg_volume: validationResult.data.counters.total,
					isValidated: true,
				};

				setValidatedLegs((prev) => ({
					...prev,
					[leg.id]: validatedData,
				}));
			}
		} catch (error) {
			console.error("Validation error:", error);
		}
	};

	if (!result?.resData?.strategy?.paths && !result?.resData?.strategy?.response?.paths) return null;

	return (
		<>
			<div className={classes.tableContainer}>
				<table className={classes.table}>
					<thead className={classes.header}>
						{table.getHeaderGroups().map((headerGroup) => (
							<tr key={headerGroup.id}>
								{headerGroup.headers.map((header) => (
									<th
										key={header.id}
										className={classes.cell}
										style={{ width: header.getSize() }}
										onClick={header.column.getToggleSortingHandler()}
									>
										<div style={{ display: "flex", alignItems: "center" }}>
											{flexRender(header.column.columnDef.header, header.getContext())}
											{{
												asc: <Icon style={{ fontSize: 16, marginLeft: 4 }}>arrow_upward</Icon>,
												desc: <Icon style={{ fontSize: 16, marginLeft: 4 }}>arrow_downward</Icon>,
											}[header.column.getIsSorted()] ?? null}
										</div>
									</th>
								))}
							</tr>
						))}
					</thead>
					<tbody>
						{table.getRowModel().rows.map((row) =>
							row.original.isSubRow ? null : (
								<React.Fragment key={row.id}>
									<tr
										className={`${classes.hover} ${row.original.isSubRow ? classes.subRow : ""} ${
											row.original.isSelected ? classes.selected : ""
										} ${row.original.flex ? classes.flexRow : ""}`}
									>
										{row.getVisibleCells().map((cell) => (
											<td
												key={cell.id}
												className={`${classes.cell} ${row.original.isSubRow ? classes.subRowCell : ""}`}
											>
												{flexRender(cell.column.columnDef.cell, cell.getContext())}
											</td>
										))}
									</tr>
									{row.getIsExpanded() && row.original.subRows && (
										<tr className={classes.subRow}>
											<td colSpan={columns.length} className={classes.subRowCell}>
												<div style={{ paddingLeft: 40 }}>
													<table className={classes.table} style={{ width: "100%" }}>
														<thead>
															<tr>
																{legColumns.map((column) => (
																	<th key={column.accessorKey} className={classes.cell}>
																		{column.header}
																	</th>
																))}
															</tr>
														</thead>
														<tbody>
															{row.original.subRows.map((leg) => (
																<tr
																	key={leg.id}
																	className={`${classes.hover} ${
																		validatedLegs[leg.id]?.isValidated ? classes.validatedRow : ""
																	}`}
																>
																	{legColumns.map((column) => (
																		<td key={column.accessorKey} className={classes.cell}>
																			{column.cell({ getValue: () => leg[column.accessorKey], row: { original: leg } })}
																		</td>
																	))}
																</tr>
															))}
														</tbody>
													</table>
												</div>
											</td>
										</tr>
									)}
								</React.Fragment>
							)
						)}
					</tbody>
				</table>
			</div>
			<StrategySummaryContainer ref={summaryRef} />
		</>
	);
};

const mapStateToProps = (state) => ({
	result: state.result,
});

const mapDispatchToProps = (dispatch, ownProps, state) => ({
	saveStrategy: () => dispatch(saveStrategy({ dispatch })),
	addToCart: (selectedMoves, pathId) => dispatch(addToCart(selectedMoves, pathId)),
	removeFromCart: (pathId) => dispatch(removeFromCart(pathId)),
});

export default strategyConnect(mapStateToProps, mapDispatchToProps)(StrategyList);
